import type { VisibilityLevel } from './audit';

/**
 * Brand Perception is split into three axes, each with its own editable query list,
 * its own audit lifecycle, its own SQS queue, and its own quota. See
 * [docs/brand-perception-split-plan.md](../../../docs/brand-perception-split-plan.md).
 */
export type BrandQueryAxis = 'narrative' | 'authority' | 'referral';

/**
 * Where a query came from. Determines whether GEO-Strategy regeneration may
 * overwrite / archive it.
 *   - geo_strategy_inferred : generated by QueryGeneratorService on a GEO Strategy
 *     validation event. Freely replaceable on the next regen diff.
 *   - user_added            : user typed it from scratch. Regeneration never touches it.
 *   - user_edited           : started as `geo_strategy_inferred`, user modified the text.
 *     Regeneration never touches it.
 */
export type BrandQuerySource = 'geo_strategy_inferred' | 'user_added' | 'user_edited';

export type BrandQueryStatus = 'active' | 'archived';

/**
 * Assignment of a referral query to the L1–L5 visibility ladder.
 *   - pending  : classification in flight (async after create/edit)
 *   - <Lx>     : classifier-assigned level (or generator-assigned on initial creation)
 *   - null     : field does not apply (narrative / authority)
 */
export type BrandQueryLevel = VisibilityLevel | 'pending' | null;

/**
 * First-class, user-editable query row. One DynamoDB item per row.
 *
 * Key shape:
 *   PK = `BRAND#<brandId>`
 *   SK = `QUERY#<axis>#<queryId>`
 *   GSI1: tenantId → `BRAND#<brandId>#<axis>` for tenant-scoped listing
 *
 * Cadence: a `textHash` invariant lets us detect reworded queries in per-query
 * trend sparklines — every audit result row snapshots the `textHash` at run time
 * as `queryTextHash`, so when the hash changes we render a distinct marker instead
 * of treating the reworded query as the same series.
 */
export interface BrandQuery {
  tenantId: string;
  brandId: string;
  queryId: string;
  axis: BrandQueryAxis;
  text: string;
  /** sha1 of normalized text (lowercase, whitespace-collapsed, punctuation-stripped). */
  textHash: string;
  /** L1..L5 for referral, or 'pending' while the classifier is in flight. null elsewhere. */
  level?: BrandQueryLevel;
  /** Referral only — geographic zone slug. */
  zone?: string;
  source: BrandQuerySource;
  /** Hash of the Brand Strategy + GEO Strategy snapshot when the row was last generated. */
  geoStrategyHash?: string;
  status: BrandQueryStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Per-tier caps on the NUMBER of active queries a user can maintain per axis.
 *
 * Referral is specified per-level: the effective count is
 *   `perLevel × 5 levels × Nzones` (zones come from the brand's locations).
 *
 * These are the schema-level caps enforced by the query CRUD routes. Monthly
 * run caps (how many times the user can re-run each axis) live on TierLimits
 * as `brandNarrativeRunsPerMonth` / etc.
 */
export const BRAND_QUERY_AXES: readonly BrandQueryAxis[] = ['narrative', 'authority', 'referral'] as const;

export const BRAND_QUERY_LEVELS: readonly VisibilityLevel[] = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;

export const BRAND_QUERY_TEXT_MAX_CHARS = 300;
